import type { jsPDF } from 'jspdf';

/**
 * Print-sheet composition for the canvas check PDF (owner request,
 * 2026-11-04): the downloaded PDF is a SHEET the user can cut the
 * check out of, not a bare check-sized page.
 *
 * The check image itself never changes: it is embedded at exactly
 * the check size (pdfW x pdfH inches, top of the sheet) from the
 * same lossless 600-DPI PNG capture as before. Below it the sheet
 * carries:
 *   - a dashed cut line spanning the check's full width,
 *   - a scissors marker + "Cut along this line" label,
 *   - a short, factual instruction block (how to separate the
 *     check, endorsing, deposit options, MICR care on the front;
 *     a reverse-side / endorsement note on the back).
 * The page grows downward by CHECK_SHEET_EXTRA_IN to fit that
 * block; front and back pages use the SAME sheet height so the
 * two check images sit in the same place on both sides of a
 * duplex print. No acceptance claims are made anywhere in the
 * copy — deposit options are stated as options, nothing is
 * guaranteed.
 */

export const CHECK_SHEET_EXTRA_IN = 1.9;

export const CUT_LINE_LABEL = 'Cut along this line';

export const FRONT_SHEET_HEADING = 'Depositing this check on paper';
export const FRONT_SHEET_BODY =
    'Cut along the dashed line above to separate the check from this sheet. ' +
    'Sign your name on the back of the check, inside the endorsement area, before you deposit it. ' +
    'You can deposit this check with your bank\'s mobile app by taking a photo of the front and back, ' +
    'or in person at a branch or ATM. ' +
    'Do not cut into the check itself, and keep the printed MICR line along the bottom of the check intact.';

export const BACK_SHEET_HEADING = 'The back of your check';
export const BACK_SHEET_BODY =
    'This side is the back of the check. When you print on both sides, it goes on the reverse ' +
    'of the front sheet, at the same size. ' +
    'Endorse the check by signing your name inside the endorsement area marked on this side ' +
    'before you deposit it.';

export interface CheckSheetLayout {
    /** Total page height (check height + the instruction area). */
    sheetH: number;
    /** Y of the dashed cut line (== pdfH + gap). */
    cutLineY: number;
    /** Baseline of the "Cut along this line" label. */
    labelBaselineY: number;
    /** Baseline of the instruction heading. */
    headingBaselineY: number;
    /** Baseline of the first instruction body line. */
    bodyFirstBaselineY: number;
    /** Vertical step between body lines. */
    bodyLineHeightIn: number;
    /** Left edge of the label / heading / body text. */
    textXIn: number;
    /** Wrap width for the body text. */
    textWidthIn: number;
}

const TEXT_X_IN = 0.3;
const CUT_GAP_IN = 0.16;
const LABEL_BASELINE_OFF_IN = 0.175;
const HEADING_BASELINE_OFF_IN = 0.44;
const BODY_FIRST_BASELINE_OFF_IN = 0.63;
const BODY_LINE_HEIGHT_IN = 0.155;
export const SHEET_BOTTOM_PAD_IN = 0.22;

export function checkSheetLayout(pdfW: number, pdfH: number): CheckSheetLayout {
    const cutLineY = pdfH + CUT_GAP_IN;
    return {
        sheetH: pdfH + CHECK_SHEET_EXTRA_IN,
        cutLineY,
        labelBaselineY: cutLineY + LABEL_BASELINE_OFF_IN,
        headingBaselineY: cutLineY + HEADING_BASELINE_OFF_IN,
        bodyFirstBaselineY: cutLineY + BODY_FIRST_BASELINE_OFF_IN,
        bodyLineHeightIn: BODY_LINE_HEIGHT_IN,
        textXIn: TEXT_X_IN,
        textWidthIn: pdfW - TEXT_X_IN * 2,
    };
}

/**
 * The scissors marker is drawn as vector strokes (two blades, two
 * handle rings) rather than a font glyph: jsPDF's standard fonts
 * have no scissors character (U+2702 is outside WinAnsi, and the
 * ZapfDingbats code renders blank in common PDF viewers), so a
 * glyph would silently vanish for many users. Strokes render
 * identically everywhere.
 */
function drawScissorsMarker(doc: jsPDF, cx: number, cy: number): void {
    doc.setDrawColor(80, 80, 80);
    doc.setLineWidth(0.013);
    // Blades: two strokes crossing at the pivot, tips to the right.
    doc.line(cx - 0.052, cy - 0.05, cx + 0.088, cy + 0.037);
    doc.line(cx - 0.052, cy + 0.05, cx + 0.088, cy - 0.037);
    // Handle rings at the rear (left) ends of the blades.
    doc.circle(cx - 0.07, cy - 0.057, 0.02, 'S');
    doc.circle(cx - 0.07, cy + 0.057, 0.02, 'S');
}

/**
 * Draw one sheet page: the check image at its exact size at the
 * top, then the cut line and instruction block below it. The
 * caller owns page creation (the doc's pages must already be
 * sized [pdfW, layout.sheetH]).
 */
export function drawCheckSheetPage(
    doc: jsPDF,
    imageData: string,
    pdfW: number,
    pdfH: number,
    side: 'front' | 'back',
): void {
    const layout = checkSheetLayout(pdfW, pdfH);

    // The check itself — byte-for-byte the same embed as the
    // pre-sheet PDF: exact check size, top of the page, PNG.
    doc.addImage(imageData, 'PNG', 0, 0, pdfW, pdfH);

    // Dashed cut line spanning the check's full width.
    doc.setDrawColor(140, 140, 140);
    doc.setLineWidth(0.01);
    doc.setLineDashPattern([0.07, 0.05], 0);
    doc.line(0, layout.cutLineY, pdfW, layout.cutLineY);
    doc.setLineDashPattern([], 0);

    // Scissors marker + label at the line's left end.
    drawScissorsMarker(doc, layout.textXIn + 0.095, layout.labelBaselineY - 0.033);
    doc.setTextColor(80, 80, 80);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.text(CUT_LINE_LABEL, layout.textXIn + 0.21, layout.labelBaselineY);

    // Instruction heading.
    doc.setTextColor(30, 30, 30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.text(side === 'front' ? FRONT_SHEET_HEADING : BACK_SHEET_HEADING, layout.textXIn, layout.headingBaselineY);

    // Instruction body, wrapped to the sheet width, one drawn
    // line per wrapped line so the geometry is exact.
    doc.setTextColor(55, 55, 55);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const body = side === 'front' ? FRONT_SHEET_BODY : BACK_SHEET_BODY;
    const lines: string[] = doc.splitTextToSize(body, layout.textWidthIn);
    lines.forEach((line, i) => {
        doc.text(line, layout.textXIn, layout.bodyFirstBaselineY + i * layout.bodyLineHeightIn);
    });
}
